import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { useToast } from '../components/Toast';
import { useTableControls } from '../hooks/useTableControls';
import SearchBox from '../components/SearchBox';
import StatusTabs from '../components/StatusTabs';
import SortableTh from '../components/SortableTh';
import Pagination from '../components/Pagination';
import Icon from '../components/Icon';

const STATUS_TABS = [
  { value: 'all', label: 'All comments' },
  { value: 'replied', label: 'Replied' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'lead', label: 'Lead' },
  { value: 'pending', label: 'Pending' },
];

function initials(name) {
  return (name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(s => s[0].toUpperCase())
    .join('') || '?';
}

export default function CommentsPage() {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(() =>
    api.getComments()
      .then(r => { setItems(r.items || []); setLastUpdated(new Date()); })
      .catch(() => toast('Failed to load comments', 'error'))
      .finally(() => setLoading(false)), [toast]);

  useEffect(() => { load(); }, [load]);

  const tc = useTableControls({
    items,
    searchFields: (c) => [c.author_name, c.content, c.classification, c.reply_content],
    statusOf: (c) => c.status,
    sorters: {
      created_at: (c) => c.created_at || '',
      author_name: (c) => c.author_name || '',
      status: (c) => c.status || '',
    },
    defaultSort: 'created_at',
    defaultDir: 'desc',
    pageSize: 10,
  });

  return (
    <>
      <div className="page-header"><h1>Comments</h1></div>

      {!loading && items.length > 0 && (
        <section className="summary-bar">
          <div className="summary-item">
            <span className="summary-value">{items.length}</span>
            <span className="summary-label">Total comments</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item attention">
            <span className="summary-value">{tc.counts.pending || 0}</span>
            <span className="summary-label">Pending</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item danger">
            <span className="summary-value">{tc.counts.escalated || 0}</span>
            <span className="summary-label">Escalated</span>
          </div>
          <div className="summary-divider" />
          <div className="summary-item success">
            <span className="summary-value">{tc.counts.replied || 0}</span>
            <span className="summary-label">Replied</span>
          </div>
          {lastUpdated && (
            <div className="summary-updated">
              <span className="live-dot" />
              Updated {lastUpdated.toLocaleTimeString()}
            </div>
          )}
        </section>
      )}

      <section className="list-panel">
        <div className="panel-toolbar">
          <div className="panel-toolbar-title">
            <h2>All comments</h2>
            <span>{tc.sorted.length} records</span>
          </div>
          <div className="panel-toolbar-actions">
            <SearchBox value={tc.search} onChange={tc.setSearch} placeholder="Search comments…" />
            <button type="button" className="icon-btn" onClick={load} title="Refresh">
              <Icon name="refresh" />
            </button>
          </div>
        </div>

        <StatusTabs tabs={STATUS_TABS} active={tc.activeStatus} onChange={tc.setActiveStatus} counts={tc.counts} total={items.length} />

        <div className="table-wrap">
          {loading ? (
            <div className="empty-state"><p>Loading…</p></div>
          ) : items.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">💬</div>
              <p>No comments found</p>
            </div>
          ) : tc.paginated.length === 0 ? (
            <div className="empty-state"><p>No comments match your search or filters.</p></div>
          ) : (
            <table>
              <thead>
                <tr>
                  <SortableTh col="created_at" label="Date" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <SortableTh col="author_name" label="From" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <th>Comment</th>
                  <th>Classification</th>
                  <SortableTh col="status" label="Status" sortCol={tc.sortCol} sortDir={tc.sortDir} onSort={tc.handleSort} />
                  <th>Reply</th>
                </tr>
              </thead>
              <tbody>
                {tc.paginated.map(c => (
                  <tr key={c.comment_id}>
                    <td className="td-mono">{c.created_at?.slice(0, 10)}</td>
                    <td>
                      <span className="name-cell">
                        <span className="name-avatar">{initials(c.author_name)}</span>
                        {c.author_name}
                      </span>
                    </td>
                    <td className="td-truncate" style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>{c.content}</td>
                    <td><span className="badge badge-draft" style={{ fontSize: '0.72rem' }}>{c.classification}</span></td>
                    <td><span className={`badge badge-${c.status} badge-dot`}>{c.status}</span></td>
                    <td className="td-truncate" style={{ fontSize: '0.82rem', color: 'var(--teal)' }}>
                      {c.reply_content || '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Pagination
          pageSize={tc.pageSize}
          setPageSize={tc.setPageSize}
          currentPage={tc.currentPage}
          setCurrentPage={tc.setCurrentPage}
          totalPages={tc.totalPages}
          pageStart={tc.pageStart}
          pageEnd={tc.pageEnd}
          total={tc.sorted.length}
          paginationItems={tc.paginationItems}
        />
      </section>
    </>
  );
}
